// src/components/popups/PopupVendorRequestForm.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../../api'; // Ваш API клиент
import { useUser } from '../../context/UserContext'; // Ваш UserContext
import InputWithLabel from '../Form/Input/InputWithLabel'; // Используем ваш InputWithLabel
import fetchLocationByIP from '../../utils/fetchLocationByIP';

// Стили (остаются без изменений)
const commonSelectStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    appearance: 'none',
    backgroundColor: 'white',
    background: 'white url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\'%3e%3cpath fill=\'none\' stroke=\'%23333\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'2\' d=\'M2 5l6 6 6-6\'/%3e%3c/svg%3e") no-repeat right .75rem center/16px 12px'
};
const errorSelectStyle = {...commonSelectStyle, borderColor: 'red'};
const errorSpanStyle = {
    color: 'red',
    fontSize: '0.875em',
    marginTop: '4px',
    display: 'block',
    marginBottom: '10px'
};
const generalErrorStyle = {
    color: 'red',
    backgroundColor: '#ffebee',
    border: '1px solid red',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    textAlign: 'center',
    fontSize: '0.9em'
};
const textareaStyle = {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontSize: '1.5rem',
    minHeight: '100px',
    lineHeight: '1.5',
};
const errorTextareaStyle = {...textareaStyle, borderColor: 'red'};


function PopupVendorRequestForm({
    isOpen,
    onClose,
    onSubmitSuccess,
    initialStateId,
    initialCityId,
    vendorId // Проп для ID вендора
}) {
    const {user, isAuthenticated} = useUser();
    const [formData, setFormData] = useState({
        name: '', phone_number: '', email: '', message: '',
        city_id: '', state_id: '',
    });

    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoadingStates, setIsLoadingStates] = useState(false);
    const [isLoadingCities, setIsLoadingCities] = useState(false);

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [requestSent, setRequestSent] = useState(false);

    const [detectedIpLocation, setDetectedIpLocation] = useState(null);
    const [isIpDetectionDone, setIsIpDetectionDone] = useState(false);
    const [loadingIpLocation, setLoadingIpLocation] = useState(false);

    const initialDataLoadedRef = useRef(false);

    const resetFormAndInitialize = useCallback(() => {
        const userName = isAuthenticated && user ? (user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '') : '';
        const userPhone = isAuthenticated && user ? (user.phone_number || user.phone || '') : '';
        const userEmail = isAuthenticated && user ? (user.email || '') : '';

        setFormData({
            name: userName,
            phone_number: userPhone,
            email: userEmail,
            state_id: initialStateId ? String(initialStateId) : '',
            city_id: '', // Сбрасываем city_id здесь
            message: '',
        });
        setErrors({});
        setSubmitError(null); // Используем null для отсутствия ошибки
        setRequestSent(false);
    }, [isAuthenticated, user, initialStateId]);


    const fetchStates = useCallback(async () => {
        if (!isOpen || initialDataLoadedRef.current) return;

        setIsLoadingStates(true);
        setSubmitError('');
        try {
            const response = await api.get('/states-filtered/?country_id=1');
            const data = response.data;
            const validStates = Array.isArray(data) ? data : [];
            const mappedStates = [
                {value: '', label: 'Select State*'},
                ...validStates.map(s => ({value: s.state_id, label: s.name}))
            ];
            setStates(mappedStates);
            initialDataLoadedRef.current = true;

            // Установка начального штата, если он передан и валиден
            if (initialStateId) {
                const initialIdStr = String(initialStateId);
                const isInitialStateValid = mappedStates.some(s => String(s.value) === initialIdStr);

                setFormData(prevFormData => {
                    if (isInitialStateValid && prevFormData.state_id !== initialIdStr) {
                        return { ...prevFormData, state_id: initialIdStr, city_id: '' }; // Сброс города при установке штата
                    } else if (!isInitialStateValid && prevFormData.state_id !== '') {
                        return { ...prevFormData, state_id: '', city_id: '' }; // Сброс, если initialStateId невалиден
                    }
                    return prevFormData;
                });
            }
        } catch (error) {
            console.error("Не удалось загрузить штаты:", error.response?.data || error.message);
            setStates([{value: '', label: 'Could not load states'}]);
            setErrors(prev => ({...prev, state_id: 'Could not load states.'}));
        } finally {
            setIsLoadingStates(false);
        }
    }, [isOpen, initialStateId]);


    const fetchCities = useCallback(async (stateIdToFetch) => {
        if (!stateIdToFetch || !isOpen) {
            setCities([]); // Очищаем города, если нет ID штата или попап закрыт
            // formData.city_id будет очищен в handleChange при смене state_id
            return;
        }
        setIsLoadingCities(true);
        setSubmitError('');
        try {
            const response = await api.get(`/cities-filtered/?state_id=${stateIdToFetch}`);
            const data = response.data;
            const validCities = Array.isArray(data) ? data : [];
            const mappedCities = [
                {value: '', label: 'Select City*'},
                ...validCities.map(c => ({value: c.city_id, label: c.name}))
            ];
            setCities(mappedCities);

            let cityToSet = '';
            // Логика установки начального города или города по IP
            if (initialCityId && String(stateIdToFetch) === String(initialStateId)) { // Только если штат совпадает с начальным
                const isInitialCityValid = mappedCities.some(c => String(c.value) === String(initialCityId));
                if (isInitialCityValid) {
                    cityToSet = String(initialCityId);
                }
            } else if (detectedIpLocation?.city && states.length > 0) {
                const currentStateDetails = states.find(s => String(s.value) === String(stateIdToFetch));
                if (currentStateDetails?.label?.toLowerCase() === detectedIpLocation.region?.toLowerCase()) {
                    const matchedCityByIp = mappedCities.find(c => c.label?.toLowerCase() === detectedIpLocation.city?.toLowerCase());
                    if (matchedCityByIp?.value) {
                        cityToSet = String(matchedCityByIp.value);
                    }
                }
            }

            // Обновляем city_id в formData, только если cityToSet имеет значение
            // и текущий city_id пуст (чтобы не перезаписывать выбор пользователя, если он уже был сделан)
            setFormData(prev => {
                if (cityToSet && prev.city_id === '') { // Условие prev.city_id === '' важно
                    return { ...prev, city_id: cityToSet };
                }
                return prev;
            });

        } catch (error) {
            console.error("Не удалось загрузить города:", error.response?.data || error.message);
            setCities([{value: '', label: 'Could not load cities'}]);
            setErrors(prev => ({...prev, city_id: 'Could not load cities for this state.'}));
        } finally {
            setIsLoadingCities(false);
        }
    }, [isOpen, initialCityId, initialStateId, detectedIpLocation, states]); // `states` добавлено в зависимости

    // Эффект для инициализации формы и загрузки данных при открытии попапа
    useEffect(() => {
        if (isOpen) {
            console.log("PopupVendorRequestForm opened. Passed vendorId:", vendorId); // <<< ОТЛАДКА
            resetFormAndInitialize();
            initialDataLoadedRef.current = false; // Сбрасываем флаг загрузки начальных данных
            setIsIpDetectionDone(false); // Сбрасываем флаг определения IP

            setLoadingIpLocation(true);
            fetchLocationByIP()
                .then(ipData => {
                    if (ipData) {
                        setDetectedIpLocation(ipData);
                    }
                })
                .catch(err => console.error("Failed to get IP location for PopupVendorRequestForm", err))
                .finally(() => {
                    setLoadingIpLocation(false);
                    setIsIpDetectionDone(true); // Устанавливаем флаг после завершения попытки определения IP
                });

            fetchStates(); // Загружаем штаты
        } else {
            // Очистка при закрытии попапа (с небольшой задержкой для анимации)
            const timer = setTimeout(() => {
                initialDataLoadedRef.current = false;
                setStates([]);
                setCities([]);
                setDetectedIpLocation(null);
                setIsIpDetectionDone(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, resetFormAndInitialize, fetchStates, vendorId]); // vendorId добавлен в зависимости

    // Эффект для установки штата по IP после загрузки штатов и определения IP
    useEffect(() => {
        if (isOpen && isIpDetectionDone && !isLoadingStates && states.length > 1 && !formData.state_id && detectedIpLocation?.region) {
            const matchedState = states.find(s => s.label && s.label.toLowerCase() === detectedIpLocation.region.toLowerCase());
            if (matchedState?.value) {
                setFormData(prev => ({
                    ...prev,
                    state_id: String(matchedState.value),
                    city_id: '' // Сбрасываем город при установке штата по IP
                }));
            }
        }
    }, [isOpen, isIpDetectionDone, detectedIpLocation, states, isLoadingStates, formData.state_id]);

    // Эффект для загрузки городов при изменении formData.state_id
    useEffect(() => {
        if (formData.state_id && isOpen) {
            fetchCities(formData.state_id);
        } else if (!formData.state_id && isOpen) {
            setCities([]); // Очищаем города, если штат не выбран
        }
    }, [formData.state_id, fetchCities, isOpen]); // fetchCities добавлен в зависимости


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({ ...prevData, [name]: value }));
        if (errors[name]) { setErrors(prev => ({ ...prev, [name]: null })); }
        if (name === 'state_id') {
            // При смене штата, сбрасываем город и запрашиваем новые города
            setFormData(prevData => ({...prevData, city_id: ''}));
            // fetchCities(value); // Загрузка городов теперь обрабатывается в useEffect по formData.state_id
        }
        setSubmitError(null); // Сбрасываем общую ошибку отправки при любом изменении
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (formData.phone_number.trim() && !/^\+?[0-9\s\-()]{7,20}$/.test(formData.phone_number)) {
            newErrors.phone_number = 'Invalid phone number format';
        }
        if (!isAuthenticated) { // Email обязателен только для неаутентифицированных пользователей
            if (!formData.email.trim()) newErrors.email = 'Email is required';
            else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email address is invalid';
        }
        if (!formData.state_id) newErrors.state_id = 'State is required';
        if (!formData.city_id) newErrors.city_id = 'City is required';
        if (!formData.message.trim()) newErrors.message = 'Message is required';

        // --- ОТЛАДКА: Проверка vendorId перед отправкой ---
        if (!vendorId) {
            console.error("VALIDATE_FORM_ERROR: vendorId is missing or invalid!", vendorId);
            // Можно добавить ошибку в newErrors, если это критично для валидации на фронте,
            // но основная проверка будет на бэкенде.
            // newErrors.vendor_id = "Vendor information is missing.";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // --- ОТЛАДКА: Проверка vendorId при попытке отправки ---
        console.log("Submit attempt. Vendor ID in prop:", vendorId);

        setSubmitError(null); // Сбрасываем предыдущие ошибки отправки
        if (!validateForm() || isSubmitting) return;

        // --- Убедимся, что vendorId существует перед отправкой ---
        if (!vendorId) {
            console.error("CRITICAL: handleSubmit called without a valid vendorId. Aborting submission.", vendorId);
            setSubmitError("Cannot submit request: Vendor information is missing. Please try again or contact support.");
            setIsSubmitting(false); // Сбросить флаг отправки
            return;
        }

        setIsSubmitting(true);

        const dataToSend = {
            name: formData.name,
            phone_number: formData.phone_number,
            state_id: parseInt(formData.state_id, 10),
            city_id: parseInt(formData.city_id, 10),
            message: formData.message,
            vendor_id: vendorId // Передаем ID вендора
        };

        if (!isAuthenticated) {
            dataToSend.email = formData.email;
        }

        // --- ОТЛАДКА: Показываем данные перед отправкой ---
        console.log("Data being sent to backend:", dataToSend);

        try {
            const response = await api.post('/vendors-app/request-vendor-services/', dataToSend);
            // Статус 201 (Created) или 200 (OK) считаем успехом
            if (response.status === 201 || response.status === 200 ) {
                setRequestSent(true); // Устанавливаем флаг успешной отправки
                if (onSubmitSuccess) { // Вызываем колбэк успешной отправки, если он есть
                     onSubmitSuccess(response.data?.message || "Request sent successfully!");
                }
            } else {
                // Обработка других успешных, но не 200/201 статусов или специфических ошибок от API
                const errorData = response.data;
                setSubmitError(errorData?.message || errorData?.detail || 'An unexpected response occurred.');
                 if (errorData?.errors && typeof errorData.errors === 'object') {
                    const backendErrors = {};
                    for (const key in errorData.errors) {
                        // Адаптируем ключи ошибок, если они отличаются от имен полей формы
                        const formKey = key === 'city' || key === 'city_id' ? 'city_id'
                                      : key === 'state' || key === 'state_id' ? 'state_id'
                                      : key;
                        backendErrors[formKey] = Array.isArray(errorData.errors[key]) ? errorData.errors[key].join(' ') : errorData.errors[key];
                    }
                    setErrors(prev => ({...prev, ...backendErrors}));
                }
            }
        } catch (error) {
            console.error("Submit error details:", error); // Логируем полную ошибку
            const errorData = error.response?.data;
            const errorMsg = errorData?.message || errorData?.detail || (typeof errorData === 'string' ? errorData : null) || 'Failed to submit request. Please check your connection and try again.';
            setSubmitError(errorMsg);

            if (errorData?.errors && typeof errorData.errors === 'object') {
                const backendErrors = {};
                 for (const key in errorData.errors) {
                     const formKey = key === 'city' || key === 'city_id' ? 'city_id'
                                   : key === 'state' || key === 'state_id' ? 'state_id'
                                   : key;
                     backendErrors[formKey] = Array.isArray(errorData.errors[key]) ? errorData.errors[key].join(' ') : errorData.errors[key];
                 }
                 setErrors(prev => ({...prev, ...backendErrors}));
            } else if (error.response?.status === 400 && errorData?.vendor_id) {
                // Специальная обработка, если бэкенд явно говорит, что vendor_id отсутствует
                setErrors(prev => ({...prev, vendor_id: errorData.vendor_id.join(' ') }));
                setSubmitError("Vendor information is missing or invalid in the request.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseModal = useCallback(() => {
        if (!isSubmitting) { // Не закрывать, если идет отправка
            onClose();
        }
    }, [onClose, isSubmitting]);


    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className={`modal ${isOpen ? 'visible' : ''}`}>
            <div className="modal__overlay" onClick={handleCloseModal}></div>
            <div className="modal__content">
                <button onClick={handleCloseModal} className="button no-style close js--close-modal" aria-label="Close modal"
                        disabled={isSubmitting}>
                    <img src="/assets/icon--close-CN6QlgAA.svg" alt="Close"/>
                </button>
                <div className="modal__form">
                    {requestSent ? (
                        <div className="content text-center">
                            <img src="/assets/icon--sendok.svg" alt="Success"
                                 style={{width: '80px', marginBottom: '20px'}}/>
                            <h2>Request Sent!</h2>
                            <p>Your request for vendor services has been submitted. You will be contacted shortly.</p>
                            <button type='button' onClick={handleCloseModal} className="button button-black">Okay</button>
                        </div>
                    ) : (
                        <form className="content" onSubmit={handleSubmit} noValidate> {/* noValidate для HTML5 валидации, чтобы работала наша */}
                            <h2>Request Vendor Services</h2>
                            <p>Please provide your details to request vendor services.</p>

                            {submitError && <div style={generalErrorStyle}>{submitError}</div>}
                            {errors.vendor_id && <div style={generalErrorStyle}>{errors.vendor_id}</div>} {/* Отображение ошибки vendor_id */}


                            <InputWithLabel name="name" label="Your Name*" value={formData.name} onChange={handleChange}
                                            error={errors.name} required autoComplete="name"/>
                            <InputWithLabel name="phone_number" label="Your Phone Number (Optional)" type="tel"
                                            value={formData.phone_number} onChange={handleChange}
                                            error={errors.phone_number} autoComplete="tel"/>
                            {!isAuthenticated && (
                                <InputWithLabel name="email" label="Your Email*" type="email"
                                                value={formData.email} onChange={handleChange}
                                                error={errors.email} required autoComplete="email"/>
                            )}

                                <label htmlFor="vendor-req-state_id" className="label">
                                    State* {loadingIpLocation ? "(Detecting Location...)" : isLoadingStates ? "(Loading States...)" : ""}
                                </label>
                                <select
                                    id="vendor-req-state_id"
                                    name="state_id"
                                    value={formData.state_id}
                                    onChange={handleChange}
                                    style={errors.state_id ? errorSelectStyle : commonSelectStyle}
                                    disabled={loadingIpLocation || isLoadingStates || (states.length <= 1 && !isLoadingStates)} // Блокируем, если штаты не загружены или только "Select State"
                                    required >
                                    {/* <option value="">Select State*</option> Уже включено в mappedStates */}
                                    {states.map((state) => <option key={state.value || `state-${state.label}`} value={state.value}>{state.label}</option>)}
                                </select>
                                {errors.state_id && <span style={errorSpanStyle}>{errors.state_id}</span>}

                                <label htmlFor="vendor-req-city_id" className="label">
                                    City* {isLoadingCities ? "(Loading...)" : ""}
                                </label>
                                <select
                                    id="vendor-req-city_id"
                                    name="city_id"
                                    value={formData.city_id}
                                    onChange={handleChange}
                                    style={errors.city_id ? errorSelectStyle : commonSelectStyle}
                                    disabled={!formData.state_id || isLoadingCities || (cities.length <= 1 && !isLoadingCities)} // Блокируем, если города не загружены или только "Select City" или не выбран штат
                                    required >
                                    {/* <option value="">Select City*</option> Уже включено в mappedCities */}
                                    {cities.map((city) => <option key={city.value || `city-${city.label}`} value={city.value}>{city.label}</option>)}
                                </select>
                                {errors.city_id && <span style={errorSpanStyle}>{errors.city_id}</span>}
                                {!formData.state_id && cities.length <= 1 && !isLoadingCities && // Показываем подсказку, если штат не выбран
                                    <span style={{fontSize: '0.8em', color: '#777', display: 'block', marginTop: '2px'}}>Please select a state first.</span>}

                                <label htmlFor="vendor-req-message" className="label">Describe your needs*</label>
                                <textarea
                                    id="vendor-req-message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    placeholder="Please describe the service you need..."
                                    style={errors.message ? errorTextareaStyle : textareaStyle}
                                />
                                {errors.message && <span style={errorSpanStyle}>{errors.message}</span>}

                            <button type="submit" className="button button-black"
                                    disabled={isSubmitting || loadingIpLocation || isLoadingStates || isLoadingCities }>
                                {isSubmitting ? 'Submitting...' : 'Send Request'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default PopupVendorRequestForm;